import { useMemo, useState } from "react";
import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { Link } from "react-router-dom";
import { useProjectCases, useProjectLang } from "../lib/project-view";
import { useSEO } from "../lib/seo";
import "../projects.css";

const PROJECT_PRESENTATION = {
  "baki-evleri": { category: "residential", scope: { en: "Residential · Brand · Sales", az: "Yaşayış · Brend · Satış" } },
  "art-tower": { category: "residential", scope: { en: "Residential · Marketing", az: "Yaşayış · Marketinq" } },
  "25-plaza": { category: "commercial", scope: { en: "Commercial · Marketing · Leasing", az: "Biznes mərkəzi · Marketinq · İcarə" } },
  "zenith-plaza": { category: "commercial", scope: { en: "Commercial · Positioning · Leasing", az: "Biznes mərkəzi · Mövqeləndirmə · İcarə" } },
  "fexri-tower": { category: "residential", scope: { en: "Residential · Marketing · Creative", az: "Yaşayış · Marketinq · Kreativ" } },
  "cahan-holding": { category: "brand", scope: { en: "Brand · Portfolio · Creative Direction", az: "Brend · Portfel · Kreativ istiqamət" } },
  "the-wood": { category: "brand" },
  "parla-residence": { category: "residential" },
  "inci-residence": { category: "residential" },
};

const localized = (value, az) => value && typeof value === "object" ? value[az ? "az" : "en"] : value;

function ProjectCard({ project, index, href, az, t }) {
  const presentation = PROJECT_PRESENTATION[project.slug] || {};
  const scope = localized(presentation.scope, az) || project.sector;
  const client = project.slug === "baki-evleri" ? "Bakı Evləri" : project.client;
  const cover = project.slug === "baki-evleri" ? "https://i.ytimg.com/vi/Gm8BcRexbF0/hqdefault.jpg" : (project.slug === "fexri-tower" ? "/case-editorial/fexri-approved-05.webp" : project.cover);

  return (
    <motion.article
      className="editorial-project-card"
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.18 }}
      transition={{ duration: 0.58, delay: (index % 2) * 0.05, ease: [0.16, 1, 0.3, 1] }}
    >
      <Link to={href(`/work/${project.slug}`)} className="editorial-project-link">
        <div className="editorial-project-media">
          <img src={cover} alt={client} loading={index < 4 ? "eager" : "lazy"} decoding="async" />
          <div className="editorial-project-shade" aria-hidden="true" />
          <span className="editorial-project-index">{String(index + 1).padStart(2, "0")}</span>
          <div className="editorial-project-copy">
            <p>{scope}</p>
            <span className="yx-project-story">{az ? t(project.summary || "Strategiya, icra və təsir") : (project.summary || "Strategy, execution and impact")}</span>
            <h2>{client}</h2>
          </div>
          <span className="editorial-project-arrow" aria-hidden="true"><ArrowUpRight /></span>
        </div>
      </Link>
    </motion.article>
  );
}

export default function Work() {
  const { href, lang, t } = useProjectLang();
  const cases = useProjectCases();
  const az = lang === "az";
  const [filter, setFilter] = useState("all");
  const projects = cases;
  const visible = useMemo(() => filter === "all" ? projects : projects.filter((project) => PROJECT_PRESENTATION[project.slug]?.category === filter), [filter, projects]);
  const categories = [
    { id: "all", label: az ? "Hamısı" : "All" },
    { id: "residential", label: az ? "Yaşayış" : "Residential" },
    { id: "commercial", label: az ? "Biznes mərkəzləri" : "Business centres" },
    { id: "brand", label: az ? "Brend & Marketinq" : "Brand & Marketing" },
  ];

  useSEO(
    az ? "Layihələr — YUXEL" : "Projects — YUXEL",
    az ? "Strategiyadan icraya. Real şirkətlər, real biznes nəticələri." : "From strategy to execution. Real companies, real business results."
  );

  return (
    <main className="project-page editorial-work-page">
      <section className="editorial-work-intro project-container">
        <div className="editorial-work-heading">
          <h1>{az ? <>Seçilmiş işlər.<br/><span>İdeyadan təsirə.</span></> : <>Selected work.<br/><span>From ideas to impact.</span></>}</h1>
          <p>{az ? "İşləri reklam postları kimi deyil, biznes problemi, yaradılan yanaşma və təsir üzərindən təqdim edirik." : "Business challenge, strategic thinking and selected execution — not a social feed."}</p>
        </div>
        <div className="editorial-work-filters" role="tablist" aria-label={az ? "Layihə kateqoriyaları" : "Project categories"}>
          {categories.map((category) => {
            const count = category.id === "all" ? projects.length : projects.filter((project) => PROJECT_PRESENTATION[project.slug]?.category === category.id).length;
            return (
              <button
                key={category.id}
                type="button"
                role="tab"
                aria-selected={filter === category.id}
                className={filter === category.id ? "is-active" : ""}
                onClick={() => setFilter(category.id)}
              >
                {category.label}<sup>{count}</sup>
              </button>
            );
          })}
        </div>
      </section>

      <section className="editorial-work-grid project-container" aria-live="polite">
        {visible.map((project, index) => <ProjectCard key={project.slug} project={project} index={index} href={href} az={az} t={t} />)}
      </section>

      <section className="editorial-work-end project-container">
        <p>{az ? "Hər layihədə məqsəd görüntü yox, biznes nəticəsidir." : "Every project is built around a business result, not just visibility."}</p>
        <Link to={href("/contact")}>{az ? "Layihəni müzakirə edək" : "Discuss your project"}<ArrowUpRight /></Link>
      </section>
    </main>
  );
}
